import { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { Link2 } from 'lucide-react';
import { AdsProviderType, ReadCampaign, ReadPage } from '@tinct/core-api-models';
import {Avatar, AvatarFallback, AvatarImage, LongText} from '@tinct/web-components';
import { CollectionColumnHeader } from '@tinct/web-collection';
import { PageStatusBadge, PageOutdatedStatusBadge, PageTargetBadge, getEffectiveStatus, getPageDisplayIdentity } from '../../../shared';
import { formatRelativeTime } from '@tinct/core-utils';
import { PageRowActions } from '../components/page-row-actions';

function VariantUrlCell({ page }: { page: ReadPage }) {
  const url = page.status === 'published' ? page.staticUrl : null;
  if (!url) return <span className="text-muted-foreground">-</span>;

  // The URL is truncated by its start: only the variant-specific last path
  // segment is shown, the copy icon copies the full URL.
  let lastSegment = url;
  try {
    lastSegment = new URL(url).pathname.split('/').filter(Boolean).pop() ?? url;
  } catch {
    // keep the full url as fallback display
  }

  const handleCopy = (e: React.MouseEvent) => {
    // Rows navigate to the variant dashboard — copying must not.
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    toast.success('Copied link to clipboard');
  };

  return (
    <div className="flex min-w-0 max-w-[240px] items-center gap-1.5">
      <span className="truncate text-muted-foreground">{lastSegment}</span>
      <button
        type="button"
        onClick={handleCopy}
        aria-label="Copy variant URL"
        className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
      >
        <Link2 className="size-3.5" />
      </button>
    </div>
  );
}

export function useLandingPagesColumns({
  campaign,
  providerByIntegrationId,
}: {
  campaign?: ReadCampaign;
  /** Resolves an ad page's `adCampaign.integrationId` to its provider. */
  providerByIntegrationId?: Map<string, AdsProviderType>;
} = {}): ColumnDef<ReadPage>[] {
  return [
    {
      accessorKey: 'companyName',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Company" />
      ),
      cell: ({ row }) => {
        const company = row.original?.lead?.company;
        const { label: name, targetType } = getPageDisplayIdentity(row.original);
        const logoUrl = company?.logo?.url;
        const initials = name
          .split(' ')
          .map((n) => n[0])
          .join('')
          .slice(0, 2)
          .toUpperCase();

        const isExcluded = row.original.status === 'excluded';
        const integrationId = row.original.adCampaign?.integrationId;
        const providerType = integrationId
          ? providerByIntegrationId?.get(integrationId)
          : undefined;

        return (
          <div className={`flex items-center gap-3 ${isExcluded ? 'opacity-60' : ''}`}>
            <Avatar className="size-8">
              {logoUrl && <AvatarImage src={logoUrl} alt={name} />}
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
            <LongText className="max-w-[200px] font-semibold">{name}</LongText>
            <PageTargetBadge targetType={targetType} providerType={providerType} />
          </div>
        );
      },
      meta: { className: 'w-[25%]' },
    },
    {
      id: 'status',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center gap-1">
          {/* Effective status folds the live job state in — same as the
              studio's page panel, so the two surfaces never disagree. */}
          <PageStatusBadge status={getEffectiveStatus(row.original) ?? null} />
          <PageOutdatedStatusBadge status={row.original.outdatedStatus} />
        </div>
      ),
      meta: { className: 'w-[15%]' },
    },
    {
      accessorKey: 'staticUrl',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="URL" />
      ),
      cell: ({ row }) => <VariantUrlCell page={row.original} />,
      meta: { className: 'w-[20%]' },
    },
    {
      id: 'visitsCount',
      accessorFn: (row) => row.statistics?.visitsCount,
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Visits" />
      ),
      cell: ({ row }) => {
        const visitsCount = row.original.statistics?.visitsCount;
        if (visitsCount == null) return <span className="text-muted-foreground">-</span>;
        return <span>{visitsCount.toLocaleString()}</span>;
      },
      meta: { className: 'w-[10%]' },
    },
    {
      accessorKey: 'lastPublishedAt',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Published" />
      ),
      cell: ({ row }) => {
        const lastPublishedAt = row.original.lastPublishedAt;
        if (!lastPublishedAt) return <span className="text-muted-foreground">-</span>;
        return (
          <span className="text-muted-foreground">
            {formatRelativeTime(new Date(lastPublishedAt))}
          </span>
        );
      },
      meta: { className: 'w-[15%]' },
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <PageRowActions page={row.original} campaign={campaign} />
        </div>
      ),
      meta: { className: 'w-[15%]' },
    },
  ];
}
